import type { MutableState } from 'src/replicant/State';
import { createPrng } from '@play-co/replicant';
import {
  checkStepInterval,
  FatePermanentStatusEffect,
  FateState,
  maxScheduleLength,
} from './fate.ruleset';
import {
  getTimeUntilAwake,
  getWantToInviteScheduleTimestamp,
  shouldEvolve,
  shouldGetDead,
  shouldGetDying,
  shouldGetScared,
  shouldGetSick,
  shouldGetToothDecay,
  shouldWantToGoOut,
  shouldWantToInvite,
} from './fate.status';
import {
  getAttributeStatusEffect,
  getPermanentStatusEffects,
  hasPermanentStatusEffect,
  shouldBeAsleep,
} from './fate.getters';
import * as Pet from '../pet';
import getFeatures from '../ruleset/features';

/**
 * Calculate and save new pet fate
 */
export function update(state: MutableState, now: number): void {
  // Save permanent status effects from fate to pet state
  syncPermanentStatusEffects(state, now);

  // Remember start point
  const since = now;

  // Create random generation based on user id and current fateUpdateAt
  // This mean that same state and same 'now' will generate same fate
  const random = createPrng(`${state.id}-${state.fate.seed}`, now);

  const features = getFeatures(state);

  // eslint-disable-next-line no-constant-condition
  while (true) {
    // Check if we sleeping first
    const sleeping = shouldBeAsleep(state, now);

    // Do not check status effects for first 5 minutes of fate generation
    // or while in daycare
    const skipPermanentStatusEffectCheck =
      now === since || hasPermanentStatusEffect(state, 'daycare', now);

    // If we sleeping skip other status checks
    if (sleeping) {
      // Add sleeping time to current check time
      now += getTimeUntilAwake(now);

      // Keep interval offset relative to sleep period to prevent
      // spike of status updates every 5 minutes
      now += since % checkStepInterval;

      continue; // Next iteration
    }

    // Check for max schedule size
    if (now - since > maxScheduleLength) {
      break; // Exit
    }

    const attributes = {
      dirtiness: Pet.getters.getAttributeValue(state, 'dirtiness', now),
      love: Pet.getters.getAttributeValue(state, 'love', now),
      stomach: Pet.getters.getAttributeValue(state, 'stomach', now),
      urine: Pet.getters.getAttributeValue(state, 'urine', now),
    };

    const stress = Pet.getters.getStressValue(state, now, attributes);

    // Calculate only attribute based status effects without rnd for first interval
    if (skipPermanentStatusEffectCheck) {
      // Calculate attribute state
      const status = getAttributeStatusEffect(attributes, stress);
      setFateStateSchedule(state, status, now);

      // Go to next iteration
      now += checkStepInterval;
      continue;
    }

    // Check death status
    const isDead = shouldGetDead(state, random(), now, since, stress, features);
    if (isDead) {
      setFateStateSchedule(state, 'dead', now);
      break; // Exit
    }

    // Check dying status
    if (hasPermanentStatusEffect(state, 'dying', now)) {
      now += checkStepInterval;
      continue; // Next iteration
    }
    const isDying = shouldGetDying(
      state,
      random(),
      now,
      since,
      stress,
      features,
    );
    if (isDying) {
      setFateStateSchedule(state, 'dying', now);
      now += checkStepInterval;
      continue; // Next iteration
    }

    // Roll sick status effect
    if (hasPermanentStatusEffect(state, 'sick', now)) {
      now += checkStepInterval;
      continue; // Next iteration
    }
    const isSick = shouldGetSick(state, random(), now, since, stress);
    if (isSick) {
      setFateStateSchedule(state, 'sick', now);
      now += checkStepInterval;
      continue; // Next iteration
    }

    // Check if we evolving
    const isEvolving =
      !hasPermanentStatusEffect(state, 'evolving', now) &&
      shouldEvolve(state, now);
    if (isEvolving) {
      setFateStateSchedule(state, 'evolving', now);
      now += checkStepInterval;
      continue; // Next iteration
    }

    // Roll wantToGoOut status effect
    if (hasPermanentStatusEffect(state, 'wantToGoOut', now)) {
      now += checkStepInterval;
      continue; // Next iteration
    }

    const isWantToGoOut = shouldWantToGoOut(state, random(), now, features);
    if (isWantToGoOut) {
      setFateStateSchedule(state, 'wantToGoOut', now);
      now += checkStepInterval;
      continue; // Next iteration
    }

    // Roll wantToInvite status effect (soft nudge — does NOT block care)
    if (features.wantToInvite) {
      if (hasPermanentStatusEffect(state, 'wantToInvite', now)) {
        now += checkStepInterval;
        continue; // Next iteration
      }

      const isWantToInvite = shouldWantToInvite(state, random(), now, features);
      if (isWantToInvite) {
        // A/B 0468 V2 Path 1 uses a fixed `petCreatedAt + 10 min` anchor so
        // care actions can't push the D0 nudge forward across the
        // schedule wipe-and-rebuild cycle. V1 and V2 Path 2 still use the
        // loop's `now` (probability-based fire — per-step decision).
        const fireAt = getWantToInviteScheduleTimestamp(state, now, features);
        setFateStateSchedule(state, 'wantToInvite', fireAt);
        now += checkStepInterval;
        continue; // Next iteration
      }
    }

    // Roll tooth decay status effect
    if (hasPermanentStatusEffect(state, 'toothDecay', now)) {
      now += checkStepInterval;
      continue; // Next iteration
    }
    const hasToothDecay = shouldGetToothDecay(state, random(), now, since);
    if (hasToothDecay) {
      setFateStateSchedule(state, 'toothDecay', now);
      now += checkStepInterval;
      continue; // Next iteration
    }

    // Roll scared status effect
    if (hasPermanentStatusEffect(state, 'scared', now)) {
      now += checkStepInterval;
      continue; // Next iteration
    }
    const isScared = shouldGetScared(state, random(), now, since, attributes);
    if (isScared) {
      setFateStateSchedule(state, 'scared', now);
      now += checkStepInterval;
      continue; // Next iteration
    }

    // Calculate attribute state
    const status = getAttributeStatusEffect(attributes, stress);
    setFateStateSchedule(state, status, now);
    // Go to next iteration
    now += checkStepInterval;
  }
}

/**
 * Save current fate state to pet state
 */
function syncPermanentStatusEffects(state: MutableState, now: number) {
  const effects = getPermanentStatusEffects(state, now);

  const effectsIds = Object.keys(effects) as FatePermanentStatusEffect[];

  for (let index = 0; index < effectsIds.length; index++) {
    const id = effectsIds[index];
    const status = effects[id];

    // We don't want to save sleeping into state
    // Sleeping it just virtual state than real
    if (id === 'asleep') continue;

    // Don't sync status effects that have been set to a future timestamp
    if (state.pet.statusEffects[id].lastUpdatedAt > now) continue;

    // Save status effect in state
    resolveStatusEffect(state, id, status.value, status.timestamp);
  }

  // Clear old fate calculation
  state.fate.schedule = {};
  state.fate.seed = now;
}

/**
 * Set state value
 */
function setFateStateSchedule(
  state: MutableState,
  effect: FateState,
  now: number,
) {
  // Already exist
  if (state.fate.schedule[effect]) return;

  state.fate.schedule[effect] = {
    timestamp: now,
  };
}

/**
 * Remove status effect from fate
 */
export function resolveStatusEffect(
  state: MutableState,
  effect: FatePermanentStatusEffect,
  hasStatus: boolean,
  now: number,
): void {
  const { setStatusEffect } = Pet.modifiers;
  const statusEffect = state.pet.statusEffects[effect];
  const oldValue = statusEffect.value;
  const isFateStatusEffectActive = !!state.fate.schedule[effect];

  // Update lastUpdatedAt only if value was changed
  if (oldValue !== hasStatus || isFateStatusEffectActive !== hasStatus) {
    setStatusEffect(state, effect, hasStatus, now);
  }

  if (hasStatus === false) {
    delete state.fate.schedule[effect];
  }
}

/**
 * Resolve pet negative effects. Any of sickness/dying/dead status.
 */
export function resolvePetNegativeEffects(
  state: MutableState,
  now: number,
): void {
  resolveStatusEffect(state, 'sick', false, now);
  resolveStatusEffect(state, 'dead', false, now);
  resolveStatusEffect(state, 'dying', false, now);

  update(state, now);
}
